/**
 * RichTextEditor atom
 * Tiptap-based rich text editor with a floating BubbleMenu toolbar
 * Appears on text selection: Bold, Italic, Underline | Paragraph, H1–H4 | Bullet list | Table
 * Custom table extensions with full editing: add/delete rows and columns, resize columns and rows
 */

import * as React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { List, ListOrdered } from 'lucide-react';
import {
  CustomTable,
  CustomTableRow,
  CustomTableHeader,
  CustomTableCell,
} from './customTable';
import { InfoBox } from './infoBox';
import { SlashCommandExtension } from './slashCommand';
import { TableSliteControls } from './TableSliteControls';
import { cn } from '@/utils/cn';
import styles from './RichTextEditor.module.scss';
import type { RichTextEditorProps } from './RichTextEditor.types';

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  variant = 'default',
  fill = false,
}: RichTextEditorProps) {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      CustomTable,
      CustomTableRow,
      CustomTableHeader,
      CustomTableCell,
      InfoBox,
      SlashCommandExtension,
    ],
    content: value,
    editable: !disabled,
    onUpdate({ editor: e }) {
      onChange(e.getHTML());
    },
    onSelectionUpdate() {
      forceUpdate();
    },
  });

  // Sync external value changes (e.g. reset form)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const TextFormatToolbar = () => (
    <div className={styles.richTextEditor__bubble}>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('paragraph') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setParagraph().run(); }} title="Párrafo">¶</button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('heading', { level: 1 }) && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run(); }} title="Título 1">H1</button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('heading', { level: 2 }) && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }} title="Título 2">H2</button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('heading', { level: 3 }) && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run(); }} title="Título 3">H3</button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('heading', { level: 4 }) && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 4 }).run(); }} title="Título 4">H4</button>
      <span className={styles.richTextEditor__bubbleSep} />
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('bold') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }} title="Negrita"><strong>B</strong></button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('italic') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }} title="Cursiva"><span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontWeight: 'normal' }}>I</span></button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('underline') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }} title="Subrayado"><u>U</u></button>
      <span className={styles.richTextEditor__bubbleSep} />
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('bulletList') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }} title="Lista de viñetas"><List size={16} /></button>
      <button type="button" data-no-press-scale="" className={cn(styles.richTextEditor__bubbleBtn, editor.isActive('orderedList') && styles['richTextEditor__bubbleBtn--active'])} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }} title="Lista numerada"><ListOrdered size={16} /></button>
    </div>
  );

  return (
    <div className={cn(styles.richTextEditor, variant === 'plain' && styles['richTextEditor--plain'], fill && styles['richTextEditor--fill'], disabled && styles['richTextEditor--disabled'], className)}>
      {editor && (
        <>
        <TableSliteControls editor={editor} />
        <BubbleMenu
          editor={editor}
          pluginKey="bubbleMenu"
          appendTo={() => document.body}
          updateDelay={0}
          style={{ zIndex: 9999 }}
          shouldShow={({ state }) => {
            if (!state.doc || !editor.isEditable) return false;
            return !state.selection.empty;
          }}
        >
          <TextFormatToolbar />
        </BubbleMenu>
        </>
      )}

      <EditorContent
        editor={editor}
        className={styles.richTextEditor__content}
        placeholder={placeholder}
      />
    </div>
  );
}
